import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import {
  useGetNetworkMetadata,
  useGetPermissions,
} from "Flocknote/API/queries";
import { useNetworkMetadata } from "Flocknote/apiHooks";
import { useUserCanOnlyInputGifts } from "Flocknote/stateHooks";

import { useGetFundList } from "Giving/API/queries";
import { useGroups as useGroupsQuery } from "GroupsManagement/API/queries";
import { useGroups } from "GroupsManagement/hooks/useNavBarGroups";
import { create } from "zustand";

import useScrollTop from "Utilities/Hooks/useScrollTop";

import { useGetSignupsList } from "./SignupsAndEvents/API/queries";
import {
  useCanSeeGiving,
  useCanSeeHouseholdDatabase,
  useCanSeeMinistryTools,
  useCanSeeSignupsNav,
} from "./stateHooks";
import { fullScreenBreakpoint } from "./styleConstants";

const navSectionKeys = {
  nothing: "nothing",
  database: "database",
  groups: "groups",
  giving: "giving",
  signups: "signups",
  tools: "tools",
};

const NAV_SECTION_OPENED_KEY = "navBar_sectionOpened";
const SELECTED_NAV_SECTION_KEY = "navBar_selectedSection";

const { nothing, ...navRefKeys } = {
  ...navSectionKeys,
  topArea: "topArea",
  accountAndSettings: "accountAndSettings",
};

const useNavBarStore = create((set) => ({
  activeSectionKey: navSectionKeys.nothing,
  setActiveSectionKey: (newActiveSectionKey) =>
    set({ activeSectionKey: newActiveSectionKey }),
  // Tracks whether the initial section has been set. Used to suppress
  // animation on the first state change so sections appear already open.
  isInitialized: false,
  setIsInitialized: (value) => set({ isInitialized: value }),
  firstSectionKey: null,
  setFirstSectionKey: (key) => set({ firstSectionKey: key }),
  activeSectionKeySetRef: { current: null },
  navBarWrapperRef: { current: null },
  topAreaRef: { current: null },
  accountAndSettingsRef: { current: null },
  sendEmailRef: { current: null },
  sendTextRef: { current: null },
  createSignupRef: { current: null },
  createSignupLinkRef: { current: null },
  signupsAndEventsRef: { current: null },
  networkImageRef: { current: null },
  addMemberButtonRef: { current: null },
  navToggleRef: { current: null },
  userButtonRef: { current: null },
  groupCommunicationRef: { current: null },
  groupCommunicationSettingsRef: { current: null },
  reSetupLinkRef: { current: null },
  newButtonRef: { current: null },
  everyoneLinkRef: { current: null },
  addGiftsLinkRef: { current: null },
  reportBuilderLinkRef: { current: null },
  prayerRequestsLinkRef: { current: null },
  duplicateFinderLinkRef: { current: null },
  homePageLinkRef: { current: null },
  householdDatabaseRef: { current: null },
  householdDatabaseSettingsRef: { current: null },
  ministryToolsRef: { current: null },
  givingRef: { current: null },
  givingSummaryRef: { current: null },
  givingFundsRef: { current: null },
  givingCampaignsRef: { current: null },
  givingBatchesRef: { current: null },
  givingDonorsRef: { current: null },
  settingsButtonRef: { current: null },
  navBarScrollTop: 0,
  setNavBarScrollTop: (navBarScrollTop) => set({ navBarScrollTop }),
  isExpanded:
    window.document.documentElement.clientWidth > fullScreenBreakpoint,
  setIsExpanded: (newIsExanded) => set({ isExpanded: newIsExanded }),
  activeSectionsNumber: 0,
  setActiveSectionsNumber: (newActiveSectionsNumber) =>
    set({ activeSectionsNumber: newActiveSectionsNumber }),
  groupsWereAutoOpened: false,
  setGroupsWereAutoOpened: (value) => set({ groupsWereAutoOpened: value }),
  navIsScrollable: false,
  setNavIsScrollable: (value) => set({ navIsScrollable: value }),
}));

const useActiveSectionsNumber = () =>
  useNavBarStore((s) => s.activeSectionsNumber);

const useInitActiveSectionsNumber = () => {
  const canSeeMinistryTools = useCanSeeMinistryTools();
  const canSeeHouseholdDatabase = useCanSeeHouseholdDatabase();
  const canSeeGiving = useCanSeeGiving();
  const canSeeSignupsNav = useCanSeeSignupsNav();

  const activeSectionsNumber = useNavBarStore((s) => s.activeSectionsNumber);
  const setActiveSectionsNumber = useNavBarStore(
    (s) => s.setActiveSectionsNumber
  );

  useEffect(() => {
    let newActiveSectionsNumber = 1; // groups is always visible
    if (canSeeHouseholdDatabase) newActiveSectionsNumber++;
    if (canSeeGiving) newActiveSectionsNumber++;
    if (canSeeSignupsNav) newActiveSectionsNumber++;
    if (canSeeMinistryTools) newActiveSectionsNumber++;

    if (activeSectionsNumber !== newActiveSectionsNumber)
      setActiveSectionsNumber(newActiveSectionsNumber);
  });
};

const getRouteSectionKey = ({ pathname, groups, groupsIsReady, givingIsReady, signupsIsReady }) => {
  const firstPartOfPath = pathname.split("/")[1].toLowerCase();
  switch (firstPartOfPath) {
    case "householddatabase":
    case "healthreport":
    case "parishes":
    case "subnetworks":
      return navSectionKeys.database;
    case "mynotes":
    case "latestactivity":
    case "library":
    case "unolytics":
    case "groupsmanagement":
    case "everyone":
    case "superunolytics":
      if (groupsIsReady) return navSectionKeys.groups;
      return navSectionKeys.nothing;
    case "giving":
    case "campaigns":
      if (givingIsReady) return navSectionKeys.giving;
      return navSectionKeys.nothing;
    case "signups":
    case "re-setup":
      if (signupsIsReady) return navSectionKeys.signups;
      return navSectionKeys.nothing;
    case "reports":
    case "prayerportal":
    case "homepage":
      return navSectionKeys.tools;
    default:
      if (groups.find(({ shortname }) => shortname === firstPartOfPath)) {
        return navSectionKeys.groups;
      }
      return navSectionKeys.nothing;
  }
};

const useRouteSectionDeps = () => {
  const location = useLocation();
  const [{ hasFundIt }] = useNetworkMetadata();
  const { isFetched: metadataIsFetched } = useGetNetworkMetadata();
  const { isFetched: fundsIsFetched } = useGetFundList();
  const { isFetched: permissionsIsFetched } = useGetPermissions();
  const canSeeGiving = useCanSeeGiving();
  const givingIsReady = useMemo(
    () =>
      (metadataIsFetched && !hasFundIt) ||
      fundsIsFetched ||
      (permissionsIsFetched && !canSeeGiving),
    [fundsIsFetched, hasFundIt, metadataIsFetched, permissionsIsFetched, canSeeGiving]
  );
  const { isFetched: groupsIsReady } = useGroupsQuery();
  const { isFetched: signupsIsReady } = useGetSignupsList();
  const { groups } = useGroups();

  return { location, groups, groupsIsReady, givingIsReady, signupsIsReady };
};

const useRouteSectionKey = () => {
  const { location, groups, groupsIsReady, givingIsReady, signupsIsReady } =
    useRouteSectionDeps();

  return useMemo(
    () =>
      getRouteSectionKey({
        pathname: location.pathname,
        groups,
        groupsIsReady,
        givingIsReady,
        signupsIsReady,
      }),
    [location.pathname, groups, groupsIsReady, givingIsReady, signupsIsReady]
  );
};

const useInitActiveSectionKey = () => {
  const { location, groups, groupsIsReady, givingIsReady, signupsIsReady } =
    useRouteSectionDeps();

  const [, setActiveSectionKey] = useActiveSectionKey();
  const setIsInitialized = useNavBarStore((s) => s.setIsInitialized);
  const isInitialized = useNavBarStore((s) => s.isInitialized);
  const setGroupsWereAutoOpened = useNavBarStore(
    (s) => s.setGroupsWereAutoOpened
  );

  // Track the last pathname we handled to avoid re-running first-visit logic
  // when effects re-run due to other dependency changes
  const lastHandledPathnameRef = useRef(null);

  useEffect(() => {
    const pendingActiveSectionKey = getRouteSectionKey({
      pathname: location.pathname,
      groups,
      groupsIsReady,
      givingIsReady,
      signupsIsReady,
    });
    // Helper to set section and mark as initialized after first paint
    const setInitialSection = (sectionKey) => {
      setActiveSectionKey(sectionKey);
      if (!isInitialized) {
        requestAnimationFrame(() => {
          setIsInitialized(true);
        });
      }
    };

    if (pendingActiveSectionKey !== navSectionKeys.nothing) {
      setInitialSection(pendingActiveSectionKey);
    } else if (groupsIsReady) {
      // Only run first-visit logic when pathname actually changes
      // This prevents re-running when effect re-triggers from other deps
      if (lastHandledPathnameRef.current === location.pathname) {
        return;
      }
      lastHandledPathnameRef.current = location.pathname;

      // First time opening a section: auto-open Group Communications on dashboard
      // After any section has been opened: restore from session storage instead
      const hasSectionBeenOpened = sessionStorage.getItem(NAV_SECTION_OPENED_KEY);
      if (!hasSectionBeenOpened) {
        setInitialSection(navSectionKeys.groups);
        setGroupsWereAutoOpened(true);
      } else {
        // Restore previously selected section from session storage (e.g., on page refresh)
        const storedSection = sessionStorage.getItem(SELECTED_NAV_SECTION_KEY);
        if (storedSection && navSectionKeys[storedSection]) {
          setInitialSection(storedSection);
        }
      }
    }
  }, [
    location.pathname,
    setActiveSectionKey,
    groups,
    givingIsReady,
    groupsIsReady,
    signupsIsReady,
    isInitialized,
    setIsInitialized,
    setGroupsWereAutoOpened,
  ]);
};

const useActiveSectionKey = () => {
  const activeSectionKey = useNavBarStore((s) => s.activeSectionKey);
  const activeSectionKeySetRef = useActiveSectionKeySetRef();
  const setActiveSectionKeyRaw = useNavBarStore((s) => s.setActiveSectionKey);
  const setActiveSectionKey = useCallback(
    (newActiveSectionKey) => {
      activeSectionKeySetRef.current = true;
      setActiveSectionKeyRaw(newActiveSectionKey);
      // Persist to session storage so it survives refresh (including "nothing" state)
      sessionStorage.setItem(SELECTED_NAV_SECTION_KEY, newActiveSectionKey);
      // Mark that a section has been opened (prevents auto-open on subsequent dashboard visits)
      if (newActiveSectionKey !== navSectionKeys.nothing) {
        sessionStorage.setItem(NAV_SECTION_OPENED_KEY, "true");
      }
    },
    [activeSectionKeySetRef, setActiveSectionKeyRaw]
  );

  return [activeSectionKey, setActiveSectionKey];
};

const useHasActiveSection = () => {
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () => activeSectionKey !== navSectionKeys.nothing,
    [activeSectionKey]
  );
};

const useNavBarWrapperRef = () => {
  const navBarWrapperRef = useNavBarStore((s) => s.navBarWrapperRef);

  return navBarWrapperRef;
};

const useNavBarScrollTop = () => {
  const navBarScrollTop = useNavBarStore((s) => s.navBarScrollTop);
  const setNavBarScrollTop = useNavBarStore((s) => s.setNavBarScrollTop);
  const navbarWrapperRef = useNavBarWrapperRef();

  useScrollTop(navbarWrapperRef, setNavBarScrollTop);

  return navBarScrollTop;
};

const useTopAreaRef = () => {
  const topAreaRef = useNavBarStore((s) => s.topAreaRef);

  return topAreaRef;
};

const useSendEmailRef = () => {
  return useNavBarStore((s) => s.sendEmailRef);
};

const useSendTextRef = () => {
  return useNavBarStore((s) => s.sendTextRef);
};

const useAddMemberButtonRef = () => {
  return useNavBarStore((s) => s.addMemberButtonRef);
};

const useCreateSignupRef = () => {
  return useNavBarStore((s) => s.createSignupRef);
};

const useCreateSignupLinkRef = () => {
  return useNavBarStore((s) => s.createSignupLinkRef);
};

const useNetworkImageRef = () => {
  return useNavBarStore((s) => s.networkImageRef);
};

const useSignupsAndEventsRef = () => {
  return useNavBarStore((s) => s.signupsAndEventsRef);
};

const useNavToggleRef = () => {
  return useNavBarStore((s) => s.navToggleRef);
};

const useUserButtonRef = () => {
  return useNavBarStore((s) => s.userButtonRef);
};

const useGroupCommunicationRef = () => {
  return useNavBarStore((s) => s.groupCommunicationRef);
};

const useGroupCommunicationSettingsRef = () => {
  return useNavBarStore((s) => s.groupCommunicationSettingsRef);
};

const useRESetupLinkRef = () => {
  return useNavBarStore((s) => s.reSetupLinkRef);
};

const useNewButtonRef = () => {
  return useNavBarStore((s) => s.newButtonRef);
};

const useEveryoneLinkRef = () => {
  return useNavBarStore((s) => s.everyoneLinkRef);
};

const useAddGiftsLinkRef = () => {
  return useNavBarStore((s) => s.addGiftsLinkRef);
};

const useReportBuilderLinkRef = () => {
  return useNavBarStore((s) => s.reportBuilderLinkRef);
};

const usePrayerRequestsLinkRef = () => {
  return useNavBarStore((s) => s.prayerRequestsLinkRef);
};

const useDuplicateFinderLinkRef = () => {
  return useNavBarStore((s) => s.duplicateFinderLinkRef);
};

const useHomePageLinkRef = () => {
  return useNavBarStore((s) => s.homePageLinkRef);
};

const useHouseholdDatabaseRef = () => {
  return useNavBarStore((s) => s.householdDatabaseRef);
};

const useHouseholdDatabaseSettingsRef = () => {
  return useNavBarStore((s) => s.householdDatabaseSettingsRef);
};

const useMinistryToolsRef = () => {
  return useNavBarStore((s) => s.ministryToolsRef);
};

const useGivingRef = () => {
  return useNavBarStore((s) => s.givingRef);
};

const useGivingSummaryRef = () => {
  return useNavBarStore((s) => s.givingSummaryRef);
};

const useGivingFundsRef = () => {
  return useNavBarStore((s) => s.givingFundsRef);
};

const useGivingCampaignsRef = () => {
  return useNavBarStore((s) => s.givingCampaignsRef);
};

const useGivingBatchesRef = () => {
  return useNavBarStore((s) => s.givingBatchesRef);
};

const useGivingDonorsRef = () => {
  return useNavBarStore((s) => s.givingDonorsRef);
};

const useSettingsButtonRef = () => {
  return useNavBarStore((s) => s.settingsButtonRef);
};

const useAccountAndSettingsRef = () => {
  const accountAndSettingsRef = useNavBarStore((s) => s.accountAndSettingsRef);

  return accountAndSettingsRef;
};

const useActiveSectionKeySetRef = () => {
  const activeSectionKeySetRef = useNavBarStore(
    (s) => s.activeSectionKeySetRef
  );

  return activeSectionKeySetRef;
};

const useNavBarIsFullyLoaded = () => {
  const [{ hasFundIt }] = useNetworkMetadata();
  const { isFetched: fundsIsFetched } = useGetFundList();
  const canSeeGiving = useCanSeeGiving();
  const canSeeSignups = useCanSeeSignupsNav();
  const canOnlyInputGifts = useUserCanOnlyInputGifts();

  const { isFetched: groupsIsReady } = useGroupsQuery();
  const { isFetched: signupsIsFetched } = useGetSignupsList();
  const { isFetched: permissionsIsReady } = useGetPermissions();

  const givingIsReady = useMemo(
    () =>
      !hasFundIt ||
      fundsIsFetched ||
      (permissionsIsReady && (!canSeeGiving || canOnlyInputGifts)),
    [fundsIsFetched, hasFundIt, permissionsIsReady, canSeeGiving, canOnlyInputGifts]
  );

  const signupsIsReady = useMemo(
    () => signupsIsFetched || (permissionsIsReady && !canSeeSignups),
    [signupsIsFetched, permissionsIsReady, canSeeSignups]
  );

  return givingIsReady && groupsIsReady && signupsIsReady && permissionsIsReady;
};

const useIsExpanded = () => {
  const isExpanded = useNavBarStore((s) => s.isExpanded);
  const setIsExpanded = useNavBarStore((s) => s.setIsExpanded);

  return [isExpanded, setIsExpanded];
};

const useIsInitialized = () => useNavBarStore((s) => s.isInitialized);

const useGroupsWereAutoOpened = () =>
  useNavBarStore((s) => s.groupsWereAutoOpened);

const useSetGroupsWereAutoOpened = () =>
  useNavBarStore((s) => s.setGroupsWereAutoOpened);

const useNavIsScrollable = () => useNavBarStore((s) => s.navIsScrollable);
const useSetNavIsScrollable = () => useNavBarStore((s) => s.setNavIsScrollable);

const useFirstSectionKey = () => useNavBarStore((s) => s.firstSectionKey);
const useSetFirstSectionKey = () => useNavBarStore((s) => s.setFirstSectionKey);

export {
  useActiveSectionKey,
  navSectionKeys,
  useTopAreaRef,
  navRefKeys,
  useNavBarWrapperRef,
  useSendEmailRef,
  useSendTextRef,
  useAddMemberButtonRef,
  useCreateSignupRef,
  useCreateSignupLinkRef,
  useSignupsAndEventsRef,
  useRESetupLinkRef,
  useNavToggleRef,
  useUserButtonRef,
  useGroupCommunicationRef,
  useGroupCommunicationSettingsRef,
  useNewButtonRef,
  useEveryoneLinkRef,
  useAddGiftsLinkRef,
  useReportBuilderLinkRef,
  usePrayerRequestsLinkRef,
  useDuplicateFinderLinkRef,
  useHomePageLinkRef,
  useHouseholdDatabaseRef,
  useHouseholdDatabaseSettingsRef,
  useMinistryToolsRef,
  useGivingRef,
  useGivingSummaryRef,
  useGivingFundsRef,
  useGivingCampaignsRef,
  useGivingBatchesRef,
  useGivingDonorsRef,
  useSettingsButtonRef,
  useNetworkImageRef,
  useHasActiveSection,
  useAccountAndSettingsRef,
  useNavBarIsFullyLoaded,
  useNavBarScrollTop,
  useIsExpanded,
  useInitActiveSectionKey,
  useInitActiveSectionsNumber,
  useActiveSectionsNumber,
  useIsInitialized,
  useGroupsWereAutoOpened,
  useSetGroupsWereAutoOpened,
  useRouteSectionKey,
  useNavIsScrollable,
  useSetNavIsScrollable,
  useFirstSectionKey,
  useSetFirstSectionKey,
};
export default useNavBarStore;
